/**
 * 모델 없이 만드는 깊이맵.
 *
 * 신경망 다운로드(약 50MB) 없이도 앱이 즉시 동작해야 한다. 그리고 정직하게 말하면,
 * 게임용 2.5D 캐릭터에는 이쪽이 더 안전할 때가 많다 — Depth Anything은 사진에 맞춰
 * 학습돼서 셀 셰이딩 일러스트를 만나면 평면으로 뭉개거나 배경을 앞으로 끌어낸다.
 *
 * 두 가지 단서를 섞는다.
 *   1. **실루엣 거리장** — 가운데일수록 앞으로. 부풀린 인형 같은 부피를 준다.
 *   2. **셰이딩** — 밝은 곳이 앞, 어두운 골이 뒤. 저주파를 뺀 성분만 쓴다.
 *      전체 밝기 차를 그냥 쓰면 어두운 옷을 입은 캐릭터가 통째로 함몰된다.
 */

import type { Field } from '../types.ts';
import { clamp, gaussianBlur, mapField, normalizeField } from '../imaging/raster.ts';

export interface HeuristicOptions {
  /** 셰이딩 단서의 비중 0..1. 0이면 순수한 부피만. */
  shadingWeight?: number;
  /** 거리장 감마. 1보다 작으면 납작하고 넓게, 크면 뾰족하게. */
  dome?: number;
}

/**
 * 깎기용 셰이딩 깊이 — 부피(거리장) 없이 저주파를 뺀 셰이딩만.
 *
 * 깊이 깎기에 거리장 돔을 넣으면 안 된다: 백분위 정규화가 캐릭터 전체에서 이뤄져
 * 팔·다리처럼 가는 부위가 통째로 "멀다"가 되어 두께의 20% 씩 갈려 나갔다(실측, 로봇 팔).
 * 균일 조명으로 그린 턴어라운드에서 어두운 곳은 대개 골(배 아래, 턱 밑, 팔 안쪽)이니
 * 그 신호만 쓴다.
 */
export function shadingDepth(mask: Field, luma: Field): Field {
  const maxDim = Math.max(mask.width, mask.height);
  const base = gaussianBlur(luma, Math.max(4, maxDim * 0.08));
  const relief = normalizeField(mapField(luma, (v, i) => v - base.data[i]!), 0.03, 0.97, mask);
  const smoothed = gaussianBlur(relief, Math.max(1, maxDim / 200));
  return mapField(smoothed, (v, i) => clamp(v) * clamp(mask.data[i]!));
}

export function heuristicDepth(
  mask: Field,
  distance: Field,
  luma: Field,
  opts: HeuristicOptions = {},
): Field {
  const shadingWeight = opts.shadingWeight ?? 0.3;
  const dome = opts.dome ?? 0.55;
  const maxDim = Math.max(mask.width, mask.height);

  // 반구에 가까운 부피. sqrt 계열 감마가 원뿔형 아티팩트를 없앤다.
  const volume = mapField(distance, (d) => Math.pow(clamp(d), dome));

  // 저주파를 뺀 셰이딩. 국소적인 요철만 남는다.
  const base = gaussianBlur(luma, Math.max(4, maxDim * 0.08));
  const relief = normalizeField(
    mapField(luma, (v, i) => v - base.data[i]!),
    0.03,
    0.97,
    mask,
  );

  const mixed = mapField(volume, (v, i) => {
    const s = relief.data[i]! - 0.5;
    return clamp(v * (1 - shadingWeight) + shadingWeight * (v + s * 0.9));
  });

  const smoothed = gaussianBlur(mixed, Math.max(1, maxDim / 220));
  return mapField(smoothed, (v, i) => clamp(v) * clamp(mask.data[i]!));
}
